import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, TextField, Typography, IconButton, Alert } from '@mui/material';
import { CalendarToday, Cancel } from '@mui/icons-material';
import { DesktopDatePicker as DatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import moment from 'moment';

const ElectionDatesEditor = ({
  election,
  electionImplementation,
  onSave,
  onCancel,
  showSaveButton = true,
  showCancelButton = false,
  hideBallotContentDisclaimer = false,
  useStrictValidation = false,
  saving = false
}) => {
  const [ editData, setEditData ] = useState({});
  const [ error, setError ] = useState(null);

  const onChangeDate = (e) => {
    const { name, value } = e.target;
    setEditData({ ...editData, [name]: value })
  }

  // Helper to get current value (from editData, election, or electionImplementation)
  const getCurrentValue = (key) => {
    if (Object.keys(editData).includes(key)) {
      return editData[key];
    }
    return election?.[key] || electionImplementation?.[key];
  }

  // Helper to check if a date value exists (handles moment objects and date strings)
  const hasDateValue = (value) => {
    return value !== null && value !== undefined && value !== '';
  }

  // Check if required fields are complete
  const submittable = useStrictValidation ? (() => {
    const qualifyingDate = getCurrentValue('qualifyingDate');
    const voterRegistrationEnd = getCurrentValue('voterRegistrationEnd');
    const voterRegistrationAdvanceEnd = getCurrentValue('voterRegistrationAdvanceEnd');
    const electionDate = getCurrentValue('date');
    
    return (
      hasDateValue(qualifyingDate) &&
      (hasDateValue(voterRegistrationEnd) || hasDateValue(voterRegistrationAdvanceEnd)) &&
      hasDateValue(electionDate)
    );
  })() : (
    Object.keys(editData).length > 0 && 
    (
      Object.keys(editData).includes('date')
      ? Boolean(editData.date)
      : Boolean(election?.date)
    )
  );

  const valueFor = (date, keyKey) => {
    if(date[keyKey]) {
      const valueOfKey = date[keyKey];
      const valueToReturn = Object.keys(editData).includes(date[keyKey]) 
        ? editData[valueOfKey] 
        : (date?.ei ? electionImplementation?.[valueOfKey]  : election?.[valueOfKey]);
      const nameToStore = keyKey.slice(0, -3)
      return { [nameToStore]: (nameToStore.includes('date') && valueToReturn) ? moment(valueToReturn) : valueToReturn }
    } else {
      return {}
    }
  }

  const dates = useMemo(() => {
    const allDates = [
      { 
        title: (`Ballot content disclaimer`), 
        subtextKey: 'ballotContentDisclaimer',
        faqLinkKey: 'ballot-content'
      },
      { 
        title: (`Last day to register to vote`), 
        date1Key: 'voterRegistrationEnd',
        subtextKey: 'voterRegistrationDisclaimer',
        faqLinkKey: 'voter-registration'
      },
    { 
      title: (`Last day to register to vote (online or mail)`), 
      date1Key: 'voterRegistrationAdvanceEnd',
      subtextKey: 'voterRegistrationAdvanceDisclaimer',
      faqLinkKey: 'voter-registration'
    },
    { 
      title: (`Early voting`), 
      date1Key: 'earlyVotingStart',
      date2Key: 'earlyVotingEnd',
      subtextKey: 'earlyVotingDisclaimer',
      faqLinkKey: 'early-voting'
    },
    { 
      title: (`Last day to request absentee ballot`), 
      date1Key: 'absenteeEnd',
      subtextKey: 'absenteeDisclaimer',
      faqLinkKey:  'absentee-voting'
    },
      { 
        title: ('Last day to vote'), 
        date1Key: 'date',
        subtextKey: 'electionDayDisclaimer',
        faqLinkKey: 'election-day-voting'
      }
    ];
    
    // Filter out ballotContentDisclaimer if hideBallotContentDisclaimer is true
    const filteredDates = hideBallotContentDisclaimer
      ? allDates.filter(d => d.subtextKey !== 'ballotContentDisclaimer')
      : allDates;
    
    return filteredDates.map(d => {
      return {
        ...d,
        ...(valueFor(d, 'date1Key')),
        ...(valueFor(d, 'subtextKey')),
        ...(valueFor(d, 'date2Key')),
      }
    });
  }, [ editData, election, electionImplementation, hideBallotContentDisclaimer ])

  const projectDates = useMemo(() => ([
    { 
      title: (`Qualifying date`), 
      date1Key: 'qualifyingDate',
      ei: false
    },
    { 
      title: (`Candidate profiling starts on`), 
      date1Key: 'profilingStart',
      ei: true
    },
    { 
      title: (`Scouting for races starts on`), 
      date1Key: 'raceScoutingStartDate',
      ei: true
    },
    { 
      title: (`Scouting for referendums starts on`), 
      date1Key: 'measureScoutingStartDate',
      ei: true
    },
    { 
      title: (`Target go-live date`), 
      date1Key: 'goLiveDate',
      ei: true
    },
  ].map(d => {
    return {
      ...d,
      ...(valueFor(d, 'date1Key')),
      ...(valueFor(d, 'subtextKey')),
      ...(valueFor(d, 'date2Key')),
    }
  })), [ editData, election, electionImplementation ])

  const handleSave = async () => {
    const allDates = [].concat(dates, projectDates);
    try {
      const keysEdited = Object.keys(editData);
      const changesToElection = allDates.reduce((acc, d) => {
        if(!d.ei) {
          if(d.date1Key && keysEdited.includes(d.date1Key)) {
            acc[d.date1Key] = editData[d.date1Key] ? moment.utc(editData[d.date1Key]).toISOString() : null;
          }
          if(d.date2Key && keysEdited.includes(d.date2Key)) {
            acc[d.date2Key] = editData[d.date2Key] ? moment.utc(editData[d.date2Key]).toISOString() : null;
          }
          if(d.subtextKey && keysEdited.includes(d.subtextKey)) {
            // Special case for ballotContentDisclaimer - don't convert to date
            if (d.subtextKey === 'ballotContentDisclaimer') {
              acc[d.subtextKey] = editData[d.subtextKey];
            } else {
              acc[d.subtextKey] = editData[d.subtextKey];
            }
          }
        }
        return acc
      }, {})

      const changesToEi = allDates.reduce((acc, d) => {
        if(d.ei) {
          if(d.date1Key && keysEdited.includes(d.date1Key)) {
            acc[d.date1Key] = editData[d.date1Key] ? moment.utc(editData[d.date1Key]).toISOString() : null;
          }
          if(d.date2Key && keysEdited.includes(d.date2Key)) {
            acc[d.date2Key] = editData[d.date2Key] ? moment.utc(editData[d.date2Key]).toISOString() : null;
          }
          if(d.subtextKey && keysEdited.includes(d.subtextKey)) {
            acc[d.subtextKey] = editData[d.subtextKey]
          }
        }
        return acc
      }, {})

      await onSave(changesToElection, changesToEi, editData);
      setEditData({});
      setError(null);
    } catch(err) {
      console.error(err)
      setError(err)
    }
  }

  return (
    <>
      {error && (
        <Alert severity='error' style={{ marginTop: '24px', marginBottom: '24px' }}>
          {error.message || 'An error occurred while saving dates'}
        </Alert>
      )}
      <DateSection title='Election dates' dates={dates} onChange={onChangeDate}/>
      <DateSection title='Project dates' dates={projectDates} onChange={onChangeDate}/>
      {(showSaveButton || showCancelButton) && (
        <ButtonContainer>
          {showCancelButton && onCancel && (
            <Button variant='outlined' color='secondary' onClick={onCancel}>
              Cancel
            </Button>
          )}
          {showSaveButton && (
            <Button variant='contained' color='primary' disabled={!submittable || saving} onClick={handleSave}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </ButtonContainer>
      )}
    </>
  )
}

const DateSection = ({ title, dates, onChange }) => {
  return (
    <Section>
      <Typography variant='h3'>{title}</Typography>
      <div>
        {dates.map((d, i) => (
          d.date1Key ? (
            <DateRow date={d} key={i} onChange={onChange}/>
          ) : (
            <DisclaimerRow date={d} key={i} onChange={onChange}/>
          )
        ))}
      </div>
    </Section>
  )
}

const DateRow = ({ date, onChange }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '24px', marginBottom: '24px' }}>
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'row', gap: '8px'}}>
        <CalendarToday style={{ marginLeft: '-24px', fontSize: '16px' }}/>
        <Typography variant='body1' style={{ fontWeight: 'bold' }}>{date.title}</Typography>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '250px 250px', gridTemplateRows: 'auto auto', gridGap: '8px 18px', alignItems: 'center' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr 20px', gridGap: '8px', alignItems: 'center',  gridRow: '1 / 2', gridColumn: '1 / 2' }}>
          <Typography variant='body1'>Start</Typography>
          <StyledDatePicker
            inputFormat="MM/DD/YYYY"
            value={date.date1 || null}
            onChange={value => onChange({ target: { name: date.date1Key, value }})}
            renderInput={(params) => <TextField variant='small' {...params} />}
            style={{
              highlight: { color: '#FFFFFF' }
            }}
          />
          <IconButton size='small' onClick={() => onChange({ target: { name: date.date1Key, value: null }})}>
            <Cancel style={{ fontSize: '16px '}}/>
          </IconButton>
        </div>
        {
          date.date2Key &&
          <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr 20px', gridGap: '8px', alignItems: 'center',  gridRow: '1 / 2', gridColumn: '2 / 3' }}>
            <Typography variant='body1' style={{ marginLeft: '24px' }}>End</Typography>
            <StyledDatePicker
              inputFormat="MM/DD/YYYY"
              value={date.date2 || null}
              onChange={value => onChange({ target: { name: date.date2Key, value }})}
              renderInput={(params) => <TextField variant='small' {...params} />}
              style={{
                highlight: { color: '#FFFFFF' }
              }}
            />
            <IconButton size='small' onClick={() => onChange({ target: { name: date.date2Key, value: null }})}>
              <Cancel style={{ fontSize: '16px '}}/>
            </IconButton>
          </div>
        }
        {
          date.subtextKey &&
          <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr', gridGap: '8px',  alignItems: 'center',  gridRow: '2 / 3', gridColumn: '1 / 3' }}>
            <Typography variant='body1'>Subtext</Typography>
            <TextField
              variant="outlined"
              size='small'
              fullWidth
              value={date.subtext}
              onChange={onChange}
              name={date.subtextKey}
            />
          </div>
        }
      </div>
    </div>
  )
}

const DisclaimerRow = ({ date, onChange }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '24px', marginBottom: '24px' }}>
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'row', gap: '8px'}}>
        <Typography variant='body1' style={{ fontWeight: 'bold' }}>{date.title}</Typography>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr', gridGap: '8px', alignItems: 'center' }}>
        <Typography variant='body1'>Text</Typography>
        <TextField
          variant="outlined"
          size='small'
          fullWidth
          multiline
          rows={3}
          value={date.subtext}
          onChange={onChange}
          name={date.subtextKey}
        />
      </div>
    </div>
  )
}

const StyledDatePicker = styled(DatePicker)`
  .MuiInputBase-input {
    padding: 2px 8px;
  }
`;

const Section = styled.div`
  margin-top: 24px;
  margin-bottom: 48px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  margin-bottom: 24px;
`;

export default ElectionDatesEditor;
